import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { Pagination } from '../types/common/Pagination';
import type { OrderByDescriptor } from '../types/common/OrderByDescriptor';
import { buildPaginationParams, buildOrderByParams } from '../client/queryParams';
import { apiClient } from '../client/client';
import type {
  SpecConstraintViewModel,
  SpecConstraintCreateModel,
  SpecConstraintUpdateModel,
} from '../types/spec-constraint/SpecConstraintModels';

export interface SpecConstraintQuery {
  /** Case-insensitive match on Key or DisplayName (Spec-specific backend filter). */
  search?: string;
  productTypeId?: number;
  isDeleted?: boolean;
  pagination?: Pagination | null;
  orderBy?: OrderByDescriptor[] | null;
}

/**
 * SpecConstraint list uses dedicated backend filters (Search, ProductTypeId) rather than
 * the generic Name mapper used by taxonomy entities.
 */
export class SpecConstraintService {
  private static readonly BASE = '/api/spec-constraint';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async getAll(params?: SpecConstraintQuery): Promise<ServiceResultT<SpecConstraintViewModel[]>> {
    const query: Record<string, unknown> = {};
    if (params?.search) query.Search = params.search;
    if (params?.productTypeId != null) query.ProductTypeId = params.productTypeId;
    if (params?.isDeleted != null) query.IsDeleted = params.isDeleted;
    Object.assign(query, buildPaginationParams(params?.pagination));
    Object.assign(query, buildOrderByParams(params?.orderBy));
    const { data } = await this.client.get<ServiceResultT<SpecConstraintViewModel[]>>(SpecConstraintService.BASE, {
      params: Object.keys(query).length ? query : undefined,
    });
    return data;
  }

  async getById(id: number | string): Promise<ServiceResultT<SpecConstraintViewModel>> {
    const { data } = await this.client.get<ServiceResultT<SpecConstraintViewModel>>(
      `${SpecConstraintService.BASE}/${id}`
    );
    return data;
  }

  async create(payload: SpecConstraintCreateModel): Promise<ServiceResultT<SpecConstraintViewModel>> {
    const { data } = await this.client.post<ServiceResultT<SpecConstraintViewModel>>(
      SpecConstraintService.BASE,
      payload
    );
    return data;
  }

  async update(
    id: number | string,
    payload: SpecConstraintUpdateModel
  ): Promise<ServiceResultT<SpecConstraintViewModel>> {
    const { data } = await this.client.put<ServiceResultT<SpecConstraintViewModel>>(
      `${SpecConstraintService.BASE}/${id}`,
      payload
    );
    return data;
  }

  async delete(id: number | string): Promise<import('../types/common').ServiceResult> {
    const { data } = await this.client.delete<import('../types/common').ServiceResult>(
      `${SpecConstraintService.BASE}/${id}`
    );
    return data;
  }
}

export const specConstraintService = new SpecConstraintService();
